import {
  ClientPageCategory,
  CollectClientPageType,
  DeliveryClientPageType,
  QuestionType,
} from '@/constants'
import { dateHelper } from '@/helpers/dateHelper'
import { DELIVERY_STATUS, PICKUP_POINT_BOOKING_STATUS } from '@meltingpoint/shared-statuses'

export interface StatusItem {
  status: string
  date?: Date
  createdAt?: string
  isRequired?: boolean
  conflictWith?: DELIVERY_STATUS
  deliveryStatus?: DELIVERY_STATUS
}

export interface DeliveryPackage {
  id: string
  label: string
  packageId: number
  status: StatusItem[]
}

export const getDefaultRetailer = (): Retailer => {
  return {
    id: 0,
    name: '',
    logo: '',
    minScore: null,
    url: '',
    mentions: '',
    code: '',
  }
}

export interface Retailer {
  id: number
  name: string
  logo: string
  minScore: number | null
  url: string
  mentions: string
  code: string
}

export const getDefaultOrder = (): Order => {
  return {
    id: '',
    referenceNumber: '',
    createdAt: dateHelper.getDate(),
  }
}

export const getDefaultCarrier = (): Carrier => {
  return {
    name: '',
    contact: {
      phone: '',
      email: '',
    },
    logo: '',
  }
}
export const getDefaultLockerCarrier = (): LockerCarrier => {
  return {
    id: undefined,
    name: '',
    logo: '',
    code: '',
  }
}

export interface Product {
  cug?: string
  ean?: string
  label?: string
  quantity: number
  type: string
}

export interface Package {
  id: number
  quantity: number
  products: Product[]
}

export interface Order {
  id: string
  referenceNumber: string
  createdAt: Date
  packages?: Package[]
  services?: string[]
}

export const getDefaultDeliverySlot = (): DeliverySlot => {
  return {
    start: dateHelper.getDate(),
    end: dateHelper.getDate(),
  }
}

export interface DeliverySlot {
  start: Date
  end: Date
}

export const getDefaultDelivery = (): Delivery => {
  return {
    externalDeliveryId: '',
    status: 'DELIVERY_STARTED',
    deliveryDate: dateHelper.getDate(),
    pickingDate: dateHelper.getDate(),
    deliverySlot: getDefaultDeliverySlot(),
    retailer: getDefaultRetailer(),
    order: getDefaultOrder(),
    packages: [],
    deliveryStatus: [],
    delivery: {
      address: {
        postalCode: '',
      },
      contact: {
        language: '',
        timezone: '',
      },
    },
    trackingPageUrl: '',
    useMultiTracking: false,
    carrier: getDefaultCarrier(),
    lockerCarrier: getDefaultLockerCarrier(),
  }
}

export interface Delivery {
  externalDeliveryId: string
  status: string
  id?: string
  deliveryDate: Date
  pickingDate: Date
  deliverySlot: DeliverySlot
  retailer: Retailer
  storeId?: number
  order: Order
  packages: DeliveryPackage[]
  deliveryStatus: StatusItem[]
  delivery: {
    contact: {
      lastName?: string
      firstName?: string
      language: string
      timezone: string
    }
    address: { postalCode: string }
  }
  trackingPageUrl: string
  useMultiTracking: boolean
  carrier: Carrier
  lockerCarrier?: LockerCarrier
}

export interface Carrier {
  code?: string
  name?: string
  contact?: Contact
  logo?: string
  claimUrl?: string
}

export type LockerCarrier = {
  id: number | undefined
  code: string
  name: string
  logo: string
}

export interface Contact {
  phone?: string
  email?: string
}

export type ClientPageConfigHeader = {
  showLogo: boolean
  showLockerLogo: boolean
  showOrderId: boolean
  showOrderCreationDate: boolean
  showDeliveryDate: boolean
  showCarrierName: boolean
  showPickingDate: boolean
}

export type ClientPageConfigCarrierInfos = {
  showCarrierName: boolean
  showCarrierLogo: boolean
  followDelivery: boolean
  showClaimUrl: boolean
  phone: {
    type: ContactType
    number?: string
  }
  phoneLanguage?: string
  email: {
    type: ContactType
    value?: string
  }
}

export enum ContactType {
  EMPTY = 'EMPTY',
  CARRIER = 'CARRIER',
  CUSTOM = 'CUSTOM',
}

export type ClientPageConfigLockerInfos = {
  showLockerName: boolean
  showLockerLogo: boolean
  phone: {
    type: ContactType
    number?: string
  }
  email: {
    type: ContactType
    value?: string
  }
}

export type ClientPageConfigFooter = {
  phone: { type: string; number?: string }
  phoneLanguage?: string
  customerServiceUrl?: string
  legalNotice?: string
  criticalScore?: number
}

export type ContentQuestion = {
  question: string
  required: boolean
  title: string
  toggle?: boolean
  type: QuestionType
}

export type CustomContent = {
  toggle: boolean
  body: string
}

export type ClientPageConfig = {
  title: string
  header: ClientPageConfigHeader
  footer: ClientPageConfigFooter
  carrierInfos?: ClientPageConfigCarrierInfos
  lockerInfos?: ClientPageConfigLockerInfos
  content?: ContentQuestion[]
  customContent?: CustomContent
  body?: {
    informations: string
    instructions: Array<{ rank: number; title: string }>
    showInterval: boolean
    title: string
  }
  pickupPointConfig?: ClientPageConfigPickupPoint
}

export type Address = {
  city: string
  title?: string
  type?: string
  floor?: string
  country: string
  district?: string
  doorCode: string
  elevator?: boolean
  postalCode: string
  addressLine1: string
  addressLine2: string
}

export type ClientPageConfigPickupPoint = {
  theme: PageThemeCustomization
  contact: LockerPickupPointContact
  infos: LockerPickupPointInfo
  details: LockerPickupPointDetail
}

export type LockerPickupPointInfo = {
  showPinCode: boolean
  showOrderId?: boolean
  showEndCollectionDate?: boolean
}

export type LockerPickupPointDetail = {
  title: string
  showPickupPointAddress: boolean
  showPickupPointAddressAdditional?: boolean
  showPickupPointInfosAdditional?: boolean
  showPickupPointOpeningHours?: boolean
  showPickupPointClosingDates?: boolean
}

export type LockerPickupPointContact = {
  title: string
  phone: PhoneNumber
  phoneLanguage?: string
  email: Email
}

type PhoneNumber = {
  type: string
  number?: string
}

type Email = {
  type: string
  value?: string
}

export type PageThemeCustomization = {
  backgroundColor?: string
  primaryColor?: string
}

export type OpeningHours = {
  friday?: OpeningHoursInterval[]
  monday?: OpeningHoursInterval[]
  sunday?: OpeningHoursInterval[]
  tuesday?: OpeningHoursInterval[]
  saturday?: OpeningHoursInterval[]
  thursday?: OpeningHoursInterval[]
  wednesday?: OpeningHoursInterval[]
}

export type OpeningHoursInterval = {
  end: string
  start: string
}

export type Coordinates = {
  latitude: number
  longitude: number
}

export type PickupPointLight = {
  address: Address
  coordinates: Coordinates
  openingHours: OpeningHours
  closingDates?: string[]
  openingDate?: string
  finalClosingDate?: string
  type?: string
  category?: string
  accessibility?: string
}

export type LockerCarrierLight = {
  id: string | number
  name: string
  logo: string
  code: string
}

export type LoadingIdentificationLight = {
  pinCode: string
}

export type ClientPageScore = {
  required: boolean
  type: string
  note: number
  includedInCarrierScore: boolean
  question: string
}

export type ClientPageComment = {
  required: boolean
  type: string
  question: string
  answer: string
}

export type ScorePayload = {
  score?: Array<Omit<ClientPageScore, 'required'>>
  comment?: Array<Omit<ClientPageComment, 'type' | 'required'>>
  pageId?: string
}

export type ClientPage = {
  id: string
  retailerId: number | null
  type: DeliveryClientPageType | CollectClientPageType | ''
  category: ClientPageCategory
  isActive: boolean
  language: string
  config: ClientPageConfig
  createdAt: string
  updatedAt: string | null
}

export type ClientPageFilters = {
  retailerId: number
  isActive?: boolean
  type?: string
  category?: string
  language?: string
}

export type RetailerLight = {
  id: number
  logo?: string
  name: string
  administration?: {
    url?: string
    mentions?: string
  }
  minScore: number | null
}

export interface SelectItem {
  value?: string | number
  text: string
}

export interface MultiTrackingGlobalOrder {
  deliveryDate: string
  deliverySlot: {
    start: string
    end: string
  }
  pickingDate: string
  retailer: RetailerLight
  status: StatusItem[]
  createdAt: string
}

export interface MultiTracking {
  deliveries: Delivery[]
  globalOrder: MultiTrackingGlobalOrder
  languages: string[]
  pages: ClientPage[]
}

export interface Collect {
  collectStatus: StatusItem[]
  createdAt: string
  customer: { timezone: string; language: string }
  end: string | null
  exchangePlaceName: string
  externalCollectId: string
  retailerMentions: string
  retailerName: string
  retailerUrl: string
  retailerLogo: string
  start: string | null
  status: string
  firstAvailableSlot?: string
}

export interface HeaderContent {
  retailerLogo: string
  referenceNumber: string
  createdAt: string
  timezone: string
  carrierLogo?: string
  carrierName?: string
  lockerLogo?: string
  lockerName?: string
  deliveryDate?: string
  pickingDate?: string
}

export interface Slot {
  start: string | Date
  end: string | Date
  remainingCapacity?: number
  selected?: boolean
  id?: string
}

export interface SlotDto {
  start: string
  end: string
}

export type ChooseSlotPayload = {
  slot: SlotDto
}

export interface GetSlotsPayload {
  timezone: string
  fromDate: string
  toDate: string
}

export interface GetSlotsResponse {
  token: string
  initialDelivery: Delivery
  carrierSlots: CarrierSlot[]
  minDeliveryPromiseDate: string | null
}

export interface CarrierSlot {
  carrier: CarrierLight
  slots: Slot[]
}

export interface CarrierLight {
  code: string
  name: string
  type: string
}

export interface Day {
  name: string
  date: number
  slots: Slot[]
}

export interface WeekDay {
  [key: string]: any

  monday?: Day
  tuesday?: Day
  wednesday?: Day
  thursday?: Day
  friday?: Day
  saturday?: Day
  sunday?: Day
}

export interface StatusItemVue extends StatusItem {
  label: string
}

export type Customer = {
  language: string
  timezone: string
}

export type Seller = {
  phone: string
}

export type Booking = {
  seller: Seller
  status: PICKUP_POINT_BOOKING_STATUS
  customer: Customer
  start?: string
  end?: string
  orderId: string
  createdAt: string
  statuses: StatusItem[]
  retailer: RetailerLight
  store: Seller
  exchangePlace?: Seller
  hasScore?: boolean | null
  lockerCarrier?: LockerCarrierLight | null
  PickupPoint?: PickupPointLight | null
  loadingIdentification?: LoadingIdentificationLight | null
  endCollectionPeriod?: string | null
  Delivery?: Delivery
}

export interface GetUnavailableDatesPayload {
  start: string
  end: string
}

export interface UnavailableDate {
  date: string
  reasons: string[]
}

export interface GetUnavailableDatesResponse {
  unavailableDates: UnavailableDate[]
  warnings?: string[]
}
